// migrated 2026-04-27 to SDK realtime — `workspaceProject.realtime.notifications`
// dispatches through the SDK's pluggable provider. Today's wire is Supabase
// realtime via supabaseRealtimeProvider in bootShell; when the backend ships
// native socket.io realtime, swap the provider — this file doesn't change.
import { getSDK } from '../../../shared/functions/sdk.js'

/**
 * Subscribe to realtime notifications for the current user.
 * Call once from app init. Pushes new notifications into root.notifications
 * and fires a browser notification.
 *
 * Returns the unsubscribe handle directly; the previous version stashed a
 * Supabase channel on `root._notifChannel` — keep doing that so existing
 * cleanup paths (`root._notifChannel.unsubscribe?.()`) don't crash, but
 * the value now is the unsub fn (callable as a function OR via .unsubscribe).
 */
export const subscribeNotifications = function () {
  const root = this?.state?.root
  if (!root) return
  const me = root.currentUserEmail
  if (!me) return
  // Prevent double-subscribe.
  if (root._notifChannel) return

  let _unsub = null
  let _cancelled = false

  Promise.resolve(getSDK().getService?.('workspaceProject'))
    .then((wp) => {
      if (_cancelled || !wp?.realtime?.subscribeNotifications) return
      _unsub = wp.realtime.subscribeNotifications({ userEmail: me }, (_event, payload) => {
        const row = payload?.new
        if (!row) return
        const cur = root.notifications || []
        if (cur.some(n => n.id === row.id)) return
        const notif = _rowToNotif(row)
        root.update({ notifications: [notif, ...cur] })
        _showBrowserNotification(notif)
      })
    })
    .catch((err) => {
      console.warn('[subscribeNotifications] subscribe failed:', err?.message || err)
    })

  // Stash a callable handle that supports both fn() and .unsubscribe()
  // shapes so legacy cleanup paths keep working unchanged.
  const handle = () => {
    _cancelled = true
    if (typeof _unsub === 'function') {
      try { _unsub() } catch (_) {}
    }
  }
  handle.unsubscribe = handle
  root._notifChannel = handle
}

/**
 * Fetch initial unread + recent notifications via the SDK.
 */
export const fetchNotifications = async function () {
  const root = this?.state?.root
  if (!root) return
  const me = root.currentUserEmail
  if (!me) return
  try {
    const result = await getSDK().execute('workspaceProject.notifications', 'list', {})
    const data = Array.isArray(result?.data) ? result.data : (Array.isArray(result) ? result : [])
    root.update({ notifications: data.map(_rowToNotif) })
  } catch (e) {
    console.error('[fetchNotifications]', e?.message || e)
  }
}

const _rowToNotif = (r) => ({
  id: r.id,
  userEmail: r.user_email,
  type: r.type,
  title: r.title,
  body: r.body,
  href: r.href,
  metadata: r.metadata || {},
  read: !!r.read,
  createdAt: r.created_at
})

const _showBrowserNotification = (notif) => {
  if (typeof window === 'undefined') return
  if (Notification.permission !== 'granted') return
  try {
    const n = new Notification(notif.title, {
      body: notif.body || '',
      icon: '/favicon.ico',
      tag: notif.id
    })
    if (notif.href) {
      n.onclick = () => {
        window.focus()
        window.location.hash = ''
        window.location.pathname = notif.href
        n.close()
      }
    }
  } catch (e) { /* SW required in some browsers */ }
}
